import { ConfirmDialog } from '@/components/primitives/ConfirmDialog'
import { ErrorState } from '@/components/primitives/ErrorState'
import { PageHeader } from '@/components/primitives/PageHeader'
import { btnPrimary, btnSecondary } from '@/components/record-sheet/styles'
import { notifyError, queryClient } from '@/lib/query'
import {
  type MemoryItem,
  type SetupStatus,
  addMemory,
  deleteMemory,
  fetchMemory,
  saveSetup,
  updateMemory
} from '@/lib/team-memory'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Brain, Check, ChevronDown, Loader2, Pencil, Plus, Search, Sparkles, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AgentAvatar } from './AgentAvatar'

const KEY = ['team-memory']
const KINDS = [
  { key: '', label: 'Everything' },
  { key: 'rule', label: 'Rules' },
  { key: 'fact', label: 'Facts' },
  { key: 'allow', label: "Won't ask again" }
] as const

const field =
  'w-full rounded-[24px] border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white dark:focus:border-neutral-600'

/** Ten quick taps that teach the team the basics, so it stops asking. */
function TeamSetup({ setup, startOpen }: { setup: SetupStatus; startOpen: boolean }) {
  const [open, setOpen] = useState(startOpen || setup.answered === 0)
  const [answers, setAnswers] = useState<Record<string, string[]>>({})
  const [saving, setSaving] = useState(false)
  const picked = Object.values(answers).filter((v) => v.length).length

  function toggle(id: string, option: string, multi: boolean) {
    setAnswers((a) => {
      const cur = a[id] ?? []
      if (!multi) return { ...a, [id]: cur.includes(option) ? [] : [option] }
      return { ...a, [id]: cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option] }
    })
  }

  async function save() {
    setSaving(true)
    try {
      const res = await saveSetup(answers)
      toast.success(`The team learned ${res.saved} thing${res.saved === 1 ? '' : 's'}`)
      setAnswers({})
      setOpen(false)
      await queryClient.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      notifyError('Could not save', err)
    } finally {
      setSaving(false)
    }
  }

  const done = setup.answered === setup.total
  return (
    <section className="rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-black">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 text-left">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
          <Sparkles className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-white">
            {done ? 'Team setup is done' : 'Teach the team about XOBLACK'}
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {setup.answered} of {setup.total} answered · about 2 minutes, all taps
          </p>
        </div>
        <div className="hidden h-2 w-28 overflow-hidden rounded-full bg-neutral-100 sm:block dark:bg-neutral-800">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all"
            style={{ width: `${(setup.answered / setup.total) * 100}%` }}
          />
        </div>
        <ChevronDown className={cn('size-4 shrink-0 text-neutral-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="mt-5 space-y-5">
          {setup.questions.map((q, n) => (
            <fieldset key={q.id}>
              <legend className="text-sm font-bold text-neutral-900 dark:text-white">
                {n + 1}. {q.question}
                {q.multi && <span className="ml-1.5 text-xs font-normal text-neutral-500">Pick any</span>}
                {q.answered && !answers[q.id]?.length && (
                  <span className="ml-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    Answered
                  </span>
                )}
              </legend>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {q.options.map((o) => {
                  const on = (answers[q.id] ?? []).includes(o)
                  return (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(q.id, o, q.multi)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-all active:scale-95',
                        on
                          ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                          : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:border-neutral-500'
                      )}
                    >
                      {on && <Check className="size-3.5" />}
                      {o}
                    </button>
                  )
                })}
              </div>
            </fieldset>
          ))}
          <div className="flex items-center gap-3">
            <button type="button" className={btnPrimary} disabled={!picked || saving} onClick={save}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              Teach the team ({picked})
            </button>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Skip anything. Answering again replaces the old answer.
            </span>
          </div>
        </div>
      )}
    </section>
  )
}

function MemoryRow({ m, onDelete }: { m: MemoryItem; onDelete: () => void }) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(m.text)
  const [busy, setBusy] = useState(false)

  async function save(body: Parameters<typeof updateMemory>[1]) {
    setBusy(true)
    try {
      await updateMemory(m.id, body)
      setEditing(false)
      await queryClient.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      notifyError('Could not save', err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li className={cn('flex items-start gap-3 px-4 py-3', !m.active && 'opacity-50')}>
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="space-y-2">
            <textarea
              className={cn(field, 'min-h-16 resize-y')}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className={btnPrimary}
                disabled={busy || !text.trim()}
                onClick={() => save({ text })}
              >
                {busy && <Loader2 className="size-4 animate-spin" />} Save
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => {
                  setText(m.text)
                  setEditing(false)
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-neutral-900 dark:text-white">{m.text}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-semibold text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            {m.area_label}
          </span>
          {m.agent ? (
            <span className="inline-flex items-center gap-1">
              <AgentAvatar agentKey={m.agent.key} name={m.agent.name} size="xs" />
              {m.source_label} by {m.agent.name}
            </span>
          ) : (
            <span>{m.source_label}</span>
          )}
          {!m.active && <span className="font-semibold">Paused</span>}
        </div>
      </div>
      {!editing && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            role="switch"
            aria-checked={m.active}
            aria-label={m.active ? 'Pause' : 'Use again'}
            title={m.active ? 'The team follows this. Click to pause.' : 'Paused. Click to use again.'}
            disabled={busy}
            onClick={() => save({ active: !m.active })}
            className={cn(
              'relative h-6 w-10 rounded-full transition-colors',
              m.active ? 'bg-emerald-500' : 'bg-neutral-300 dark:bg-neutral-700'
            )}
          >
            <span
              className={cn(
                'absolute top-1 size-4 rounded-full bg-white shadow transition-all',
                m.active ? 'left-5' : 'left-1'
              )}
            />
          </button>
          {m.kind !== 'allow' && (
            <button
              type="button"
              aria-label="Edit"
              onClick={() => setEditing(true)}
              className="flex size-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-white"
            >
              <Pencil className="size-3.5" />
            </button>
          )}
          <button
            type="button"
            aria-label="Delete"
            onClick={onDelete}
            className="flex size-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      )}
    </li>
  )
}

/** What the team knows: every agent reads this before it works, so nothing has to be said twice. */
export function MemoryPage() {
  const [params] = useSearchParams()
  const list = useQuery({ queryKey: KEY, queryFn: fetchMemory })
  const [kind, setKind] = useState<(typeof KINDS)[number]['key']>('')
  const [area, setArea] = useState('')
  const [search, setSearch] = useState('')
  const [text, setText] = useState('')
  const [newKind, setNewKind] = useState<'rule' | 'fact'>('rule')
  const [newArea, setNewArea] = useState('general')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<MemoryItem | null>(null)

  const items = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (list.data?.items ?? []).filter(
      (m) => (!kind || m.kind === kind) && (!area || m.area === area) && (!q || m.text.toLowerCase().includes(q))
    )
  }, [list.data, kind, area, search])

  async function add() {
    if (!text.trim()) return
    setAdding(true)
    try {
      await addMemory({ text, kind: newKind, area: newArea })
      setText('')
      toast.success('The whole team knows now')
      await queryClient.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      notifyError('Could not save', err)
    } finally {
      setAdding(false)
    }
  }

  async function remove() {
    if (!removing) return
    try {
      await deleteMemory(removing.id)
      await queryClient.invalidateQueries({ queryKey: KEY })
    } catch (err) {
      notifyError('Could not delete it', err)
    } finally {
      setRemoving(null)
    }
  }

  if (list.error) return <ErrorState error={list.error} onRetry={() => list.refetch()} />
  const data = list.data

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        title="What the team knows"
        description="Every agent reads this before it works. Teach something once and nobody asks you again. They also learn from your answers in the chat."
      />

      {data ? (
        <TeamSetup setup={data.setup} startOpen={params.get('setup') === '1'} />
      ) : (
        <div className="h-24 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800" />
      )}

      <section className="space-y-3 rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-black">
        <p className="font-bold text-neutral-900 dark:text-white">Tell the team something</p>
        <textarea
          className={cn(field, 'min-h-16 resize-y')}
          value={text}
          maxLength={1000}
          placeholder="e.g. Refunds under $50 never need me. · Our biggest partner is Mama's Kitchen in Atlanta."
          onChange={(e) => setText(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          <div className="grid grid-cols-2 gap-1 rounded-full bg-neutral-100 p-1 dark:bg-neutral-900">
            {(['rule', 'fact'] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setNewKind(k)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-semibold',
                  newKind === k
                    ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-white'
                    : 'text-neutral-500 dark:text-neutral-400'
                )}
              >
                {k === 'rule' ? 'A rule to follow' : 'A fact'}
              </button>
            ))}
          </div>
          <select
            value={newArea}
            onChange={(e) => setNewArea(e.target.value)}
            aria-label="Area"
            className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
          >
            {data?.areas.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
          <button type="button" className={cn(btnPrimary, 'ml-auto')} disabled={!text.trim() || adding} onClick={add}>
            {adding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            Add
          </button>
        </div>
      </section>

      <section className="rounded-[36px] border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-black">
        <div className="flex flex-wrap items-center gap-2 px-1 pb-3">
          {KINDS.map((k) => (
            <button
              key={k.key || 'all'}
              type="button"
              aria-pressed={kind === k.key}
              onClick={() => setKind(k.key)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                kind === k.key
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-black'
                  : 'bg-neutral-100 text-neutral-600 hover:text-neutral-900 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:text-white'
              )}
            >
              {k.label}
            </button>
          ))}
          <select
            value={area}
            onChange={(e) => setArea(e.target.value)}
            aria-label="Filter by area"
            className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
          >
            <option value="">All areas</option>
            {data?.areas.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
          <label className="relative ml-auto min-w-[160px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-neutral-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              aria-label="Search what the team knows"
              className="w-full rounded-full border border-neutral-200 bg-white py-1.5 pl-8 pr-3 text-sm outline-none focus:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white"
            />
          </label>
        </div>
        {!data ? (
          <div className="space-y-2 p-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <Brain className="size-8 text-neutral-300 dark:text-neutral-700" />
            <p className="font-semibold text-neutral-900 dark:text-white">
              {data.items.length ? 'Nothing matches.' : 'The team doesn’t know anything yet'}
            </p>
            {!data.items.length && (
              <p className="max-w-sm text-sm text-neutral-500 dark:text-neutral-400">
                Do the team setup above, or tick “Don’t ask me this again” on a card in the chat.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100 border-t border-neutral-100 dark:divide-neutral-900 dark:border-neutral-900">
            {items.map((m) => (
              <MemoryRow key={m.id} m={m} onDelete={() => setRemoving(m)} />
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Forget this?"
        description={removing?.text ?? ''}
        confirmLabel="Forget it"
        destructive
        onConfirm={remove}
      />
    </div>
  )
}
